import React from 'react';
import PropTypes from 'prop-types';
import { Meteor } from 'meteor/meteor';
import { withTracker } from 'meteor/react-meteor-data';
import { Autocomplete, Box, FormControl, IconButton, TextField, SvgIcon } from '@mui/material';
import { isEmpty, cloneDeep, isEqual, map } from 'lodash';
import SaveIcon from '@mui/icons-material/Save';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useTranslation } from 'react-i18next';
import ShowListIconsFontawesome from '../../components/ShowListIconsFontawesome';
import { LIBRARY_ICON_TYPES } from '../../../api/AnnotationTemplates/AnnotationTemplates';
import Layers from '../../../api/Layers/Layers';
import { renderCheckBox } from '../../components/MaterialMUIComponent';

const PythonIcon = props => {
  return (
    <SvgIcon {...props}>
      <svg viewBox="0 0 64 64" fill="none" xmlns="http://www.w3.org/2000/svg">
        <g id="SVGRepo_bgCarrier" strokeWidth="0" />
        <g id="SVGRepo_tracerCarrier" strokeLinecap="round" strokeLinejoin="round" />
        <g id="SVGRepo_iconCarrier">
          <path
            d="M31.885 16c-8.124 0-7.617 3.523-7.617 3.523l.01 3.65h7.752v1.095H21.197S16 23.678 16 31.876c0 8.196 4.537 7.906 4.537 7.906h2.708v-3.804s-.146-4.537 4.465-4.537h7.688s4.32.07 4.32-4.175v-7.019S40.374 16 31.885 16zm-4.275 2.454c.771 0 1.395.624 1.395 1.395s-.624 1.395-1.395 1.395a1.393 1.393 0 0 1-1.395-1.395c0-.771.624-1.395 1.395-1.395z"
            fill="url(#a)"
          />
          <path
            d="M32.115 47.833c8.124 0 7.617-3.523 7.617-3.523l-.01-3.65H31.97v-1.095h10.832S48 40.155 48 31.958c0-8.197-4.537-7.906-4.537-7.906h-2.708v3.803s.146 4.537-4.465 4.537h-7.688s-4.32-.07-4.32 4.175v7.019s-.656 4.247 7.833 4.247zm4.275-2.454a1.393 1.393 0 0 1-1.395-1.395c0-.77.624-1.394 1.395-1.394s1.395.623 1.395 1.394c0 .772-.624 1.395-1.395 1.395z"
            fill="url(#b)"
          />
          <defs>
            <linearGradient
              id="a"
              x1="19.075"
              y1="18.782"
              x2="34.898"
              y2="34.658"
              gradientUnits="userSpaceOnUse"
            >
              <stop stopColor="#387EB8" />
              <stop offset="1" stopColor="#366994" />
            </linearGradient>
            <linearGradient
              id="b"
              x1="28.809"
              y1="28.882"
              x2="45.803"
              y2="45.163"
              gradientUnits="userSpaceOnUse"
            >
              <stop stopColor="#FFE052" />
              <stop offset="1" stopColor="#FFC331" />
            </linearGradient>
          </defs>
        </g>
      </svg>
    </SvgIcon>
  );
};

const MapsAdvancedPythonField = ({
  layers,
  listComponentsAdvanced,
  settingsAdvanced,
  setListComponentsAdvanced,
  setSettingsAdvanced
}) => {
  const [nameComponent, setNameComponent] = React.useState(
    listComponentsAdvanced.PythonComponent.name
  );
  const [openModal, setOpenModal] = React.useState(false);
  const [iconFontawesome, setIconFontawesome] = React.useState(
    listComponentsAdvanced.PythonComponent.iconFontawesome
  );
  const [iconSvg, setIconSvg] = React.useState(listComponentsAdvanced.PythonComponent.iconSvg);
  const [libraryIcon, setLibraryIcon] = React.useState(
    listComponentsAdvanced.PythonComponent.libraryIcon
  );
  const [localSettings, setLocalSettings] = React.useState(settingsAdvanced.pythonSettings);
  const handleIconSelected = (elementIcon, library) => {
    if (library === LIBRARY_ICON_TYPES.FONTAWESOME) {
      setIconFontawesome(elementIcon);
      setListComponentsAdvanced({
        ...listComponentsAdvanced,
        PythonComponent: {
          name: listComponentsAdvanced.PythonComponent.name,
          show: listComponentsAdvanced.PythonComponent.show,
          iconFontawesome: elementIcon,
          libraryIcon: library
        }
      });
    }

    if (library === LIBRARY_ICON_TYPES.SVG) {
      setIconSvg(elementIcon.url);
      setIconFontawesome(elementIcon.name);
      setListComponentsAdvanced({
        ...listComponentsAdvanced,
        PythonComponent: {
          name: listComponentsAdvanced.PythonComponent.name,
          show: listComponentsAdvanced.PythonComponent.show,
          iconFontawesome: elementIcon.name,
          iconSvg: elementIcon.url,
          libraryIcon: library,
          cetegorySvg: elementIcon.category
        }
      });
    }
    setLibraryIcon(library);
  };
  const { t } = useTranslation();

  const handleChange = (variable, newValue) => {
    setSettingsAdvanced(prev => ({
      ...prev,
      pythonSettings: {
        ...prev.pythonSettings,
        [variable]: newValue
      }
    }));
    setLocalSettings({
      ...localSettings,
      [variable]: newValue
    });
  };

  React.useEffect(() => {
    if (!isEmpty(localSettings)) {
      const checkSchema = cloneDeep(localSettings);
      map(Object.keys(checkSchema), item => {
        if (!isEqual(item, 'layerIDs')) delete checkSchema[item];
      });
      setSettingsAdvanced(prev => ({
        ...prev,
        pythonSettings: checkSchema
      }));
      setLocalSettings(checkSchema);
    }
  }, []);

  return (
    <Box display="flex" width="100%" gab={1} justifyContent="space-around">
      <Box display="column" width="40%" gab={1}>
        <Box
          component="form"
          sx={{
            display: 'flex',
            mb: 2,
            width: '100%'
          }}
          noValidate
          autoComplete="off"
        >
          <TextField
            id="outlined-required"
            label="Nombre del componente"
            size="small"
            value={nameComponent}
            onChange={event => setNameComponent(event.target.value)}
            mb={2}
            fullWidth
          />
          <IconButton
            onClick={() => setOpenModal(true)}
            style={{
              color: '#2A3F90'
            }}
          >
            {!isEmpty(libraryIcon) ? (
              <>
                {libraryIcon === LIBRARY_ICON_TYPES.FONTAWESOME && (
                  <FontAwesomeIcon icon={iconFontawesome} />
                )}
                {libraryIcon === LIBRARY_ICON_TYPES.SVG && (
                  <img
                    src={iconSvg}
                    alt={iconFontawesome}
                    style={{ marginRight: '5px', width: '20px', height: '20px' }}
                  />
                )}
              </>
            ) : (
              <PythonIcon fontSize="large" style={{ color: '#2A3F90' }} />
            )}
          </IconButton>
          <IconButton
            onClick={() => {
              setListComponentsAdvanced({
                ...listComponentsAdvanced,
                PythonComponent: {
                  name: nameComponent,
                  show: listComponentsAdvanced.PythonComponent.show
                }
              });
            }}
            style={{
              color: '#2A3F90'
            }}
          >
            <SaveIcon />
          </IconButton>
          <ShowListIconsFontawesome
            openModal={openModal}
            setOpenModal={setOpenModal}
            handleIconSelected={handleIconSelected}
            selectedIcon={iconFontawesome}
            startLibrary={libraryIcon}
            svg={iconSvg}
            startCategorySvg={listComponentsAdvanced.PythonComponent.cetegorySvg}
          />
        </Box>
        <FormControl fullWidth sx={{ mb: 2 }}>
          <Autocomplete
            multiple
            limitTags={2}
            options={layers}
            getOptionLabel={option => option.name}
            filterOptions={(options, { inputValue }) =>
              options.filter(option => option.name.toLowerCase().includes(inputValue.toLowerCase()))
            }
            value={layers.filter(layer => localSettings.layerIDs.includes(layer._id))}
            onChange={(event, newValue) => {
              if (newValue.length <= 2) {
                const newArray = newValue.map(layer => layer._id);
                handleChange('layerIDs', newArray);
              }
            }}
            isOptionEqualToValue={(option, value) => option._id === value._id}
            size="small"
            renderInput={params => <TextField {...params} label="Selecciona 2 capas" />}
            renderOption={(props, option) => (
              <li {...props} key={option._id}>
                {option.name}
              </li>
            )}
            disableCloseOnSelect
          />
        </FormControl>
        <Box mb={1}>
          {renderCheckBox({
            normalName: `${t('show_change_layer')}`,
            onChange: () => handleChange('showChangeLayer', !localSettings.showChangeLayer),
            value: localSettings.showChangeLayer
          })}
        </Box>
      </Box>
      <Box display="column" width="40%" gab={1} />
    </Box>
  );
};

export default withTracker(() => {
  // GEOJSON_API_BASED_SUBTYPES es un grupo en Layers --> ir integrando layers hasta tenerlas todas
  const fields = { _id: 1, name: 1 };
  const layersSub = Meteor.subscribe('layers.allGeoJSON', fields);
  const layers = Layers.find({ layerSubType: 'GeoJSON' }, { fields }).fetch();
  const loading = !layersSub.ready() || layers.length === 0;

  return {
    loading,
    layers
  };
})(MapsAdvancedPythonField);

MapsAdvancedPythonField.defaultProps = {};
MapsAdvancedPythonField.propTypes = {
  layers: PropTypes.array.isRequired,
  setListComponentsAdvanced: PropTypes.func.isRequired,
  listComponentsAdvanced: PropTypes.object.isRequired,
  settingsAdvanced: PropTypes.object.isRequired,
  setSettingsAdvanced: PropTypes.func.isRequired
};
